import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Cookie, X, Check, Shield, Settings, ChevronDown, ChevronUp, Lock, BarChart3, Target, Palette } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'turkish_emarket_cookie_consent';

const TRANSLATIONS = {
  tr: {
    title: 'Çerez Politikası',
    description: 'Size en iyi deneyimi sunmak için çerezler kullanıyoruz.',
    acceptAll: 'Tümünü Kabul Et',
    rejectAll: 'Sadece Zorunlu',
    customise: 'Özelleştir',
    savePreferences: 'Tercihleri Kaydet',
    privacyPolicy: 'Gizlilik Politikası',
    cancel: 'İptal',
    required: 'Zorunlu',
    categories: {
      necessary: { name: 'Zorunlu Çerezler', description: 'Bu çerezler web sitesinin çalışması için gereklidir.' },
      analytics: { name: 'Analitik Çerezler', description: 'Ziyaretçilerin siteyle nasıl etkileşime girdiği hakkında bilgi toplar.' },
      marketing: { name: 'Pazarlama Çerezleri', description: 'İlgi alanlarınıza uygun içerik için kullanılabilir.' },
      preferences: { name: 'Tercih Çerezleri', description: 'Dil tercihi gibi ayarlarınızı hatırlamamıza yardımcı olur.' },
    },
  },
  en: {
    title: 'Cookie Policy',
    description: 'We use cookies to provide you with the best experience.',
    acceptAll: 'Accept All',
    rejectAll: 'Necessary Only',
    customise: 'Customise',
    savePreferences: 'Save Preferences',
    privacyPolicy: 'Privacy Policy',
    cancel: 'Cancel',
    required: 'Required',
    categories: {
      necessary: { name: 'Necessary Cookies', description: 'These cookies are required for the website to function.' },
      analytics: { name: 'Analytics Cookies', description: 'These cookies collect information about how visitors interact with the website.' },
      marketing: { name: 'Marketing Cookies', description: 'These cookies are used by our advertising partners to build a profile of your interests.' },
      preferences: { name: 'Preference Cookies', description: 'These cookies help us remember your settings such as language preference or location.' },
    },
  },
  de: {
    title: 'Cookie-Richtlinie',
    description: 'Wir verwenden Cookies, um Ihnen das beste Erlebnis zu bieten.',
    acceptAll: 'Alle Akzeptieren',
    rejectAll: 'Nur Notwendige',
    customise: 'Anpassen',
    savePreferences: 'Einstellungen Speichern',
    privacyPolicy: 'Datenschutzrichtlinie',
    cancel: 'Abbrechen',
    required: 'Erforderlich',
    categories: {
      necessary: { name: 'Notwendige Cookies', description: 'Diese Cookies sind für die Funktion der Website erforderlich.' },
      analytics: { name: 'Analyse-Cookies', description: 'Diese Cookies sammeln Informationen darüber, wie Besucher mit der Website interagieren.' },
      marketing: { name: 'Marketing-Cookies', description: 'Diese Cookies werden von unseren Werbepartnern verwendet.' },
      preferences: { name: 'Präferenz-Cookies', description: 'Diese Cookies helfen uns, Ihre Einstellungen zu speichern.' },
    },
  },
};

const COUNTRY_LANGUAGE_MAP = {
  TR: 'tr', US: 'en', GB: 'en', DE: 'de', AT: 'de', CH: 'de',
};

const COOKIE_CATEGORIES = ['necessary', 'analytics', 'marketing', 'preferences'];
const CATEGORY_ICONS = { necessary: Lock, analytics: BarChart3, marketing: Target, preferences: Palette };

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100dvh',
  maxWidth: '100vw',
  margin: 0,
  padding: '16px',
  boxSizing: 'border-box',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'auto',
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [language, setLanguage] = useState('tr');
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: true,
  });
  const [expandedCategory, setExpandedCategory] = useState(null);

  const t = TRANSLATIONS[language] || TRANSLATIONS.en;

  useEffect(() => {
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (consent) return;

      const bl = navigator.language?.split('-')[0];
      if (bl && TRANSLATIONS[bl]) setLanguage(bl);

      const stored = sessionStorage.getItem('detected_country');
      if (stored && COUNTRY_LANGUAGE_MAP[stored]) {
        setLanguage(COUNTRY_LANGUAGE_MAP[stored]);
      }

      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    } catch {
      /* localStorage kapalı */
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    setShowSettings(false);
  };

  const saveConsent = (payload) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
    dismiss();
  };

  const handleAcceptAll = () => {
    const all = {};
    COOKIE_CATEGORIES.forEach((c) => {
      all[c] = true;
    });
    saveConsent({ accepted: true, preferences: all, timestamp: new Date().toISOString() });
  };

  const handleRejectAll = () => {
    const only = {};
    COOKIE_CATEGORIES.forEach((c) => {
      only[c] = c === 'necessary';
    });
    saveConsent({ accepted: false, preferences: only, timestamp: new Date().toISOString() });
  };

  const handleSavePreferences = () => {
    saveConsent({ accepted: true, preferences, timestamp: new Date().toISOString() });
  };

  if (!visible) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-overlay"
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleRejectAll();
      }}
    >
      <div
        data-testid="cookie-consent-banner"
        className="w-full max-w-md max-h-[85dvh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 rounded-t-2xl bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />

        <div className="p-5">
          <div className="flex items-start gap-3 mb-5">
            <div className="p-2.5 bg-orange-50 rounded-xl shrink-0">
              <Cookie className="w-7 h-7 text-orange-600" />
            </div>
            <div>
              <h2 id="cookie-consent-title" className="text-xl font-bold text-gray-900 mb-1">
                {t.title}
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t.description}{' '}
                <a href="/privacy" className="text-orange-600 underline">
                  {t.privacyPolicy}
                </a>
              </p>
            </div>
          </div>

          {showSettings && (
            <div className="mb-4 space-y-2">
              {COOKIE_CATEGORIES.map((catId) => {
                const Icon = CATEGORY_ICONS[catId];
                const cat = t.categories[catId];
                const isRequired = catId === 'necessary';
                const isExpanded = expandedCategory === catId;

                return (
                  <div
                    key={catId}
                    className={`border rounded-xl overflow-hidden ${isRequired ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    <button
                      type="button"
                      className="w-full p-3 flex items-center justify-between text-left"
                      onClick={() => setExpandedCategory(isExpanded ? null : catId)}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${isRequired ? 'text-gray-500' : 'text-orange-600'}`} />
                        <span className="font-medium text-sm">{cat.name}</span>
                        {isRequired && (
                          <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                            {t.required}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={preferences[catId]}
                          disabled={isRequired}
                          onChange={() =>
                            !isRequired &&
                            setPreferences((p) => ({ ...p, [catId]: !p[catId] }))
                          }
                          className="w-4 h-4 accent-orange-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <p className="px-3 pb-3 pl-10 text-sm text-gray-600">{cat.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!showSettings ? (
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="accept-all-cookies"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg"
              >
                <Check className="w-5 h-5" />
                {t.acceptAll}
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="reject-cookies"
                className="w-full py-3.5 rounded-xl border-2 border-gray-300 bg-white text-gray-800 font-semibold flex items-center justify-center gap-2"
              >
                <Shield className="w-5 h-5" />
                {t.rejectAll}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                data-testid="customize-cookies"
                className="w-full py-3 text-gray-600 font-medium flex items-center justify-center gap-2"
              >
                <Settings className="w-5 h-5" />
                {t.customise}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={handleSavePreferences}
                data-testid="save-cookie-preferences"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {t.savePreferences}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="w-full py-3.5 rounded-xl border-2 border-gray-300 bg-white text-gray-800 font-semibold flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                {t.cancel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const isCookieCategoryEnabled = (categoryId) => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return categoryId === 'necessary';
    const parsed = JSON.parse(consent);
    return parsed.preferences?.[categoryId] === true;
  } catch {
    return categoryId === 'necessary';
  }
};

export default CookieConsent;
